import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  obtenerModelo, obtenerHistorialPrecios, obtenerEspecificaciones, listarUsados,
  obtenerMisClasificaciones, guardarMiClasificacion,
} from './api/catalogo';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { toast } from 'react-toastify';
import './DetalleModelo.css';
import {
  Calendar, CreditCard, DollarSign, Info, Ship, Settings, Eraser, ArrowLeft, Lock, Pencil, Check, X,
} from 'lucide-react';

const STOP_TOKENS = new Set([
  'auto', 'automatico', 'automatica', 'manual', 'mt', 'at',
  'turbo', 'biturbo', 'diesel', 'bencina', 'gasolina',
  'full', 'sport', 'premium', 'comfort', 'comfortline', 'limited', 'plus',
  'awd', '4x4', '4x2', 'cv', 'hp', 'lt', 'lts', 'rs',
  'nuevo', 'usado', 'used', 'hyundai',
]);

function DetalleModelo() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [modeloSeleccionado, setModeloSeleccionado] = useState(null);
  const [historialPrecios, setHistorialPrecios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [datosGrafico, setDatosGrafico] = useState([]);
  const [filtroTipo, setFiltroTipo] = useState('Todos');
  const [filtroFechaDesde, setFiltroFechaDesde] = useState('');
  const [filtroFechaHasta, setFiltroFechaHasta] = useState('');
  const [pestanaActiva, setPestanaActiva] = useState('actual');
  const [usados, setUsados] = useState([]);
  const [loadingUsados, setLoadingUsados] = useState(false);
  const [resumenUsados, setResumenUsados] = useState(null);
  const [datosGraficoUsadosPrecio, setDatosGraficoUsadosPrecio] = useState([]);
  const [datosGraficoUsadosKm, setDatosGraficoUsadosKm] = useState([]);
  const [ratioDepreciacion, setRatioDepreciacion] = useState(null);
  const [specs, setSpecs] = useState(null);
  const [loadingSpecs, setLoadingSpecs] = useState(false);

  // ── Mi clasificación (privada, solo la ve este usuario) ─────────────────
  const [misClasificaciones, setMisClasificaciones] = useState({}); // { vehicleVersionId: texto }
  const [editandoClasificacion, setEditandoClasificacion] = useState(false);
  const [clasificacionInput, setClasificacionInput] = useState('');
  const [guardandoClasificacion, setGuardandoClasificacion] = useState(false);

  const handleVolver = () => { if (window.history.length > 1) navigate(-1); else navigate('/modelos'); };

  const precioToNumber = (valor) => {
    if (typeof valor === 'string') return Number(valor.replace(/\D+/g, '')) || 0;
    return Number(valor || 0) || 0;
  };

  const aplicaFiltros = (items) => items.filter(item => {
    const cumpleTipo = filtroTipo === 'Todos' || item.tipo === filtroTipo;
    const fechaItem = new Date(item.fechaISO + 'T00:00:00');
    const desde = filtroFechaDesde ? new Date(filtroFechaDesde + 'T00:00:00') : null;
    const hasta = filtroFechaHasta ? new Date(filtroFechaHasta + 'T23:59:59') : null;
    return cumpleTipo && (!desde || fechaItem >= desde) && (!hasta || fechaItem <= hasta);
  });

  const normalize = (s = '') =>
    String(s).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();

  const getModelTokens = (model, detail) => {
    const text = normalize(`${model || ''} ${detail || ''}`);
    return Array.from(new Set(text.split(' ').filter(t => t.length >= 2 && !STOP_TOKENS.has(t) && !/^\d+$/.test(t))));
  };

  const pickPrimaryToken = (tokens, model) => {
    if (!tokens?.length) return null;
    const modelNorm = normalize(model || '');
    for (const t of tokens) if (modelNorm.includes(t)) return t;
    return [...tokens].sort((a, b) => b.length - a.length)[0];
  };

  const clpFmt = (n) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(n);

  // ── Cargar modelo ─────────────────────────────────────────────────────────
  useEffect(() => {
    obtenerModelo(id).then(data => {
      if (data) setModeloSeleccionado(data);
    }).catch(console.error);
  }, [id]);

  useEffect(() => {
    obtenerMisClasificaciones().then(setMisClasificaciones).catch(console.error);
  }, [id]);

  const miClasificacion = misClasificaciones[id] || '';
  const sugerenciasClasificacion = [...new Set(Object.values(misClasificaciones))].filter(Boolean).sort();

  const iniciarEdicionClasificacion = () => {
    setClasificacionInput(miClasificacion);
    setEditandoClasificacion(true);
  };
  const guardarClasificacion = async () => {
    const texto = clasificacionInput.trim();
    setGuardandoClasificacion(true);
    try {
      await guardarMiClasificacion(id, texto);
      setMisClasificaciones(prev => {
        const next = { ...prev };
        if (texto) next[id] = texto; else delete next[id];
        return next;
      });
      setEditandoClasificacion(false);
      toast.success(texto ? 'Clasificación guardada' : 'Clasificación eliminada');
    } catch (e) {
      console.error(e);
      toast.error('No se pudo guardar tu clasificación');
    } finally {
      setGuardandoClasificacion(false);
    }
  };

  // ── Cargar historial ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!modeloSeleccionado) return;
    setLoading(true);
    const cargar = async () => {
      try {
        const { historial } = await obtenerHistorialPrecios(
          modeloSeleccionado.marca, modeloSeleccionado.model, modeloSeleccionado.modelDetail
        );
        const expanded = historial.flatMap(data => {
          const ts = Number(data.date_add || 0);
          const fechaISO = new Date(ts * 1000).toISOString().slice(0, 10);
          const fecha = new Intl.DateTimeFormat('es-CL').format(new Date(fechaISO + 'T00:00:00'));
          return (data.tiposprecio || []).map((tipo, i) => ({
            key: `${fechaISO}__${tipo}`, rowKey: `${fechaISO}__${tipo}`,
            fechaISO, fecha, ts, tipo, precio: (data.precio || [])[i] ?? 0,
          }));
        });
        const map = new Map();
        for (const item of expanded) {
          const prev = map.get(item.key);
          if (!prev || item.ts > prev.ts) map.set(item.key, item);
        }
        const historialDedupe = Array.from(map.values()).sort((a, b) => a.ts - b.ts);
        const fechasDesc = [...new Set(historialDedupe.map(p => p.fechaISO))].sort((a, b) => b.localeCompare(a));
        const ultimas3 = new Set(fechasDesc.slice(0, 3));
        setHistorialPrecios(historialDedupe.filter(p => ultimas3.has(p.fechaISO)).sort((a, b) => a.ts - b.ts));
        const filtrado = aplicaFiltros(historialDedupe);
        const fechasUnicas = [...new Set(filtrado.map(p => p.fechaISO))].sort((a, b) => a.localeCompare(b));
        setDatosGrafico(fechasUnicas.map(fechaISO => {
          const fecha = new Intl.DateTimeFormat('es-CL').format(new Date(fechaISO + 'T00:00:00'));
          const fila = { fecha };
          filtrado.filter(p => p.fechaISO === fechaISO).forEach(p => { fila[p.tipo] = precioToNumber(p.precio); });
          return fila;
        }));
      } catch (e) { console.error(e); } finally { setLoading(false); }
    };
    cargar();
  }, [modeloSeleccionado, filtroTipo, filtroFechaDesde, filtroFechaHasta]);

  // ── Cargar specs ──────────────────────────────────────────────────────────
  // El scoring de marca/modelo/versión ya lo hace obtenerEspecificaciones()
  // server-side (mismo criterio que usan las funciones de IA para esto).
  useEffect(() => {
    if (!modeloSeleccionado) return;
    setLoadingSpecs(true);
    const cargar = async () => {
      try {
        const resultado = await obtenerEspecificaciones(
          modeloSeleccionado.marca, modeloSeleccionado.model, modeloSeleccionado.modelDetail
        );
        setSpecs(resultado);
      } catch (e) {
        console.error('Error cargando specs:', e);
        setSpecs(null);
      } finally {
        setLoadingSpecs(false);
      }
    };
    cargar();
  }, [modeloSeleccionado]);

  // ── Cargar usados ────────────────────────────────────────────────────────
  // Marca: normalizada → query exacta. Model: varía en casing → filtro en memoria.
  useEffect(() => {
    if (!modeloSeleccionado) return;
    setLoadingUsados(true);
    const cargar = async () => {
      try {
        const marca = (modeloSeleccionado.marca || '').trim();
        const model = (modeloSeleccionado.model || '').trim();
        const detail = (modeloSeleccionado.modelDetail || '').trim();

        const { usados: todos } = await listarUsados({ marca, limit: 1000 });

        if (!todos.length) {
          console.log('Sin usados para marca:', marca);
          setUsados([]);
          return;
        }

        const modelNorm = normalize(model);

        // Prioridad 1: coincidencia exacta normalizada (mismo texto, distinto casing)
        let items = todos.filter(u =>
          normalize(u.model || u.modelo || '') === modelNorm
        );

        // Prioridad 2: uno comienza con el otro (cubre variantes con sufijos)
        if (!items.length) {
          items = todos.filter(u => {
            const uNorm = normalize(u.model || u.modelo || '');
            return uNorm.length >= 3 && modelNorm.length >= 3 &&
              (uNorm.startsWith(modelNorm) || modelNorm.startsWith(uNorm));
          });
        }

        console.log('Usados ' + marca + ' ' + model + ': ' + items.length + ' de ' + todos.length);

        // Filtro opcional de versión — solo si hay suficiente coincidencia
        if (items.length && detail) {
          const detailNorm = normalize(detail);
          const conVer = items.filter(u => {
            const uVer = normalize(u.version || u.modelDetail || u.versionDetail || '');
            return uVer.length >= 3 && (uVer.includes(detailNorm) || detailNorm.includes(uVer));
          });
          if (conVer.length >= Math.max(1, Math.ceil(items.length * 0.25))) {
            items = conVer;
          }
        }

        // Filtrar precio 0 o inválido (error de scraping)
        const toNumP = v => typeof v === 'number' ? v : Number(String(v || '0').replace(/[^0-9]/g, '')) || 0;
        items = items.filter(u => toNumP(u.precio) > 0);

        // Ordenar: año desc, precio asc
        items.sort((a, b) => {
          const ya = Number(a.anio || a.year || 0);
          const yb = Number(b.anio || b.year || 0);
          if (ya !== yb) return yb - ya;
          const pa = toNumP(a.precio);
          const pb = toNumP(b.precio);
          return pa - pb;
        });

        setUsados(items);
      } catch (e) {
        console.error('Error cargando usados:', e);
        toast.error('Error al cargar usados');
        setUsados([]);
      } finally {
        setLoadingUsados(false);
      }
    };
    cargar();
  }, [modeloSeleccionado]);

  // ── Resumen + gráficos usados ─────────────────────────────────────────────
  useEffect(() => {
    if (!usados?.length) { setResumenUsados(null); setDatosGraficoUsadosPrecio([]); setDatosGraficoUsadosKm([]); setRatioDepreciacion(null); return; }
    const toNum = v => typeof v === 'number' ? v : typeof v === 'string' ? Number(v.replace(/\D+/g, '')) || 0 : Number(v || 0) || 0;
    const median = arr => { if (!arr.length) return 0; const s = [...arr].sort((a, b) => a - b); const mid = Math.floor(s.length / 2); return s.length % 2 ? s[mid] : Math.round((s[mid - 1] + s[mid]) / 2); };
    const precios = usados.map(x => toNum(x.precio)).filter(n => n > 0);
    const kms = usados.map(x => toNum(x.kilometraje ?? x.kilometros)).filter(n => n > 0);
    const anios = usados.map(x => Number(x.anio ?? x.year)).filter(n => Number.isFinite(n));
    setResumenUsados({
      total: usados.length,
      precioMin: precios.length ? Math.min(...precios) : 0, precioMax: precios.length ? Math.max(...precios) : 0,
      precioAvg: precios.length ? Math.round(precios.reduce((a, b) => a + b, 0) / precios.length) : 0,
      precioMediana: median(precios),
      kmMin: kms.length ? Math.min(...kms) : 0, kmMax: kms.length ? Math.max(...kms) : 0,
      kmAvg: kms.length ? Math.round(kms.reduce((a, b) => a + b, 0) / kms.length) : 0,
      anioMin: anios.length ? Math.min(...anios) : null, anioMax: anios.length ? Math.max(...anios) : null,
    });
    const byYear = new Map();
    for (const it of usados) {
      const y = Number(it.anio ?? it.year); if (!Number.isFinite(y)) continue;
      const p = toNum(it.precio), k = toNum(it.kilometraje ?? it.kilometros);
      const prev = byYear.get(y) || { anio: y, _pSum: 0, _pN: 0, _kSum: 0, _kN: 0, count: 0 };
      if (p > 0) { prev._pSum += p; prev._pN += 1; }
      if (k > 0) { prev._kSum += k; prev._kN += 1; }
      prev.count += 1; byYear.set(y, prev);
    }
    const years = Array.from(byYear.values()).sort((a, b) => a.anio - b.anio)
      .map(r => ({ anio: String(r.anio), cantidad: r.count, precioProm: r._pN ? Math.round(r._pSum / r._pN) : 0, kmProm: r._kN ? Math.round(r._kSum / r._kN) : 0 }));
    setDatosGraficoUsadosPrecio(years); setDatosGraficoUsadosKm(years);
    const currentYear = new Date().getFullYear();
    const rows = usados.map(u => {
      const price = toNum(u.precio), km = toNum(u.kilometraje ?? u.kilometros ?? u.km ?? u.mileage);
      const year = Number(u.anio ?? u.year ?? u.ano ?? u.Año);
      if (!price || !km || !Number.isFinite(year)) return null;
      return { price, km, age: Math.max(0, currentYear - year) };
    }).filter(Boolean);
    if (rows.length >= 5) {
      const XT_X = [[0, 0, 0], [0, 0, 0], [0, 0, 0]], XT_y = [0, 0, 0];
      for (const r of rows) {
        XT_X[0][0] += 1; XT_X[0][1] += r.age; XT_X[0][2] += r.km;
        XT_X[1][0] += r.age; XT_X[1][1] += r.age * r.age; XT_X[1][2] += r.age * r.km;
        XT_X[2][0] += r.km; XT_X[2][1] += r.age * r.km; XT_X[2][2] += r.km * r.km;
        XT_y[0] += r.price; XT_y[1] += r.age * r.price; XT_y[2] += r.km * r.price;
      }
      const inv3 = m => {
        const [a, b, c] = [m[0][0], m[0][1], m[0][2]], [d, e, f] = [m[1][0], m[1][1], m[1][2]], [g, h, i] = [m[2][0], m[2][1], m[2][2]];
        const A = e * i - f * h, B = -(d * i - f * g), C = d * h - e * g, D = -(b * i - c * h), E = a * i - c * g, F = -(a * h - b * g), G = b * f - c * e, H = -(a * f - c * d), I = a * e - b * d;
        const det = a * A + b * B + c * C; if (!det || Math.abs(det) < 1e-12) return null;
        const inv = 1 / det; return [[A * inv, D * inv, G * inv], [B * inv, E * inv, H * inv], [C * inv, F * inv, I * inv]];
      };
      const inv = inv3(XT_X);
      if (inv) {
        const b0 = inv[0][0] * XT_y[0] + inv[0][1] * XT_y[1] + inv[0][2] * XT_y[2];
        const b1 = inv[1][0] * XT_y[0] + inv[1][1] * XT_y[1] + inv[1][2] * XT_y[2];
        const b2 = inv[2][0] * XT_y[0] + inv[2][1] * XT_y[1] + inv[2][2] * XT_y[2];
        const depAnualCLP = -b1, depPorKmCLP = -b2;
        const kmMed = median(rows.map(r => r.km)), ageMed = median(rows.map(r => r.age));
        const priceRef = Math.max(1, Math.round(b0 + b1 * ageMed + b2 * kmMed));
        setRatioDepreciacion({ depAnualCLP: Math.round(depAnualCLP), depAnualPct: Number(((depAnualCLP / priceRef) * 100).toFixed(2)), depPorKmCLP: Number(depPorKmCLP.toFixed(2)), priceRef, ageRef: ageMed, kmRef: kmMed, n: rows.length });
      } else setRatioDepreciacion(null);
    } else setRatioDepreciacion(null);
  }, [usados]);

  const historialFiltrado = aplicaFiltros(historialPrecios);
  const limpiarFiltros = () => { setFiltroTipo('Todos'); setFiltroFechaDesde(''); setFiltroFechaHasta(''); };

  // ── Spec components ───────────────────────────────────────────────────────
  const KVCard = ({ label, value }) => (
    <div className="spec-kv-card">
      <div className="spec-kv-label">{label}</div>
      {value === null || value === undefined || value === ''
        ? <div className="spec-kv-empty">—</div>
        : <div className="spec-kv-value">{String(value)}</div>}
    </div>
  );

  const SpecSection = ({ titulo, icon, children, cols = 'repeat(auto-fit, minmax(180px, 1fr))' }) => (
    <div className="spec-section">
      <div className="spec-section-header">
        {icon && <div className="spec-section-icon">{icon}</div>}
        <span className="spec-section-title">{titulo}</span>
      </div>
      <div className="spec-section-grid" style={{ gridTemplateColumns: cols }}>{children}</div>
    </div>
  );

  const EquipItem = ({ item }) => {
    const esObj = item && typeof item === 'object' && 'caracteristica' in item;
    if (esObj) return (
      <div className="spec-equip-item">
        <div className={`spec-equip-check${item.valor ? '' : ' absent'}`}>
          {item.valor && <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L3.5 7.5L8.5 2.5" stroke="#16a34a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>}
        </div>
        <span className={`spec-equip-text${item.valor ? '' : ' absent'}`}>{item.caracteristica}</span>
      </div>
    );
    return <div className="spec-list-item"><div className="spec-list-dot" /><span>{String(item)}</span></div>;
  };

  const catIconMap = {
    comfort: { emoji: '🛋', label: 'Confort' }, exterior: { emoji: '🚗', label: 'Exterior' },
    interior: { emoji: '🪑', label: 'Interior' }, seguridad: { emoji: '🛡', label: 'Seguridad' },
    multimedia: { emoji: '📱', label: 'Multimedia' }, tecnologia: { emoji: '⚙️', label: 'Tecnología' },
    adas: { emoji: '🤖', label: 'ADAS' },
  };

  const EquipCard = ({ categoria, items }) => {
    if (!items || (Array.isArray(items) && !items.length)) return null;
    const meta = catIconMap[categoria] || { emoji: '•', label: categoria };
    return (
      <div className="spec-equip-card">
        <div className="spec-equip-header">
          <span className="spec-equip-emoji">{meta.emoji}</span>
          <span className="spec-equip-cat-label">{meta.label}</span>
          {Array.isArray(items) && <span className="spec-equip-count">{items.length} ítems</span>}
        </div>
        <div className="spec-equip-body">
          {Array.isArray(items)
            ? items.map((item, i) => <EquipItem key={i} item={item} />)
            : <span style={{ fontSize: 13 }}>{String(items)}</span>}
        </div>
      </div>
    );
  };

  const TABS = [
    { key: 'actual', icon: <Info size={14} />, label: 'Precios' },
    { key: 'fob', icon: <Ship size={14} />, label: 'FOB' },
    { key: 'caracteristicas', icon: <Settings size={14} />, label: 'Características' },
    { key: 'usados', icon: <DollarSign size={14} />, label: 'Usados' },
  ];

  const CHART_COLORS = ['#378ADD', '#1D9E75', '#EF9F27', '#D85A30'];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <main className="detalle-main">
      <div className="detalle-wrapper">

        {/* Back */}
        <button onClick={handleVolver} className="detalle-back-btn">
          <ArrowLeft size={14} /> Volver
        </button>

        {modeloSeleccionado && (
          <>
            {/* Hero */}
            <div className="detalle-hero">
              <div className="detalle-hero-info">
                <span className="detalle-hero-marca">{modeloSeleccionado.marca}</span>
                <h1 className="detalle-hero-modelo">{modeloSeleccionado.model}</h1>
                <p className="detalle-hero-version">{modeloSeleccionado.modelDetail}</p>
              </div>
              {modeloSeleccionado.imagen && (
                <img src={modeloSeleccionado.imagen} alt={modeloSeleccionado.modelDetail} className="detalle-hero-img" />
              )}
            </div>

            {/* Tabs */}
            <div className="detalle-tabs">
              {TABS.map(({ key, icon, label }) => (
                <button
                  key={key}
                  className={`detalle-tab ${pestanaActiva === key ? 'active' : ''}`}
                  onClick={() => setPestanaActiva(key)}
                >
                  {icon} {label}
                </button>
              ))}
            </div>

            {/* ── TAB: Precios ── */}
            {pestanaActiva === 'actual' && (
              <div className="detalle-tab-content">
                <div className="detalle-card">
                  <div className="filtros-tabla">
                    <label>
                      Tipo
                      <select value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
                        <option value="Todos">Todos</option>
                        {[...new Set(historialPrecios.map(p => p.tipo))].map(tipo => (
                          <option key={tipo} value={tipo}>{tipo}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Desde
                      <input type="date" value={filtroFechaDesde} onChange={e => setFiltroFechaDesde(e.target.value)} />
                    </label>
                    <label>
                      Hasta
                      <input type="date" value={filtroFechaHasta} onChange={e => setFiltroFechaHasta(e.target.value)} />
                    </label>
                    <button className="btn-limpiar-filtros" onClick={limpiarFiltros}>
                      <Eraser size={13} /> Limpiar
                    </button>
                  </div>

                  <div className="historial-tabla-wrapper">
                    {loading && <div className="historial-loading">Cargando historial…</div>}
                    <table className="historial-tabla">
                      <thead>
                        <tr>
                          <th><span className="historial-th-inner"><Calendar size={13} /> Fecha</span></th>
                          <th><span className="historial-th-inner"><CreditCard size={13} /> Tipo</span></th>
                          <th className="text-right"><span className="historial-th-inner historial-th-right"><DollarSign size={13} /> Precio</span></th>
                        </tr>
                      </thead>
                      <tbody>
                        {historialFiltrado.map((item, idx) => {
                          const precioNum = precioToNumber(item.precio);
                          const tipoSlug = item.tipo.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim().replace(/\s+/g, '-').replace(/[^\w-]/g, '');
                          return (
                            <tr key={item.rowKey} className={idx % 2 !== 0 ? 'historial-fila-alt' : ''}>
                              <td>{item.fecha}</td>
                              <td><span className={`historial-tipo-badge historial-tipo-${tipoSlug}`}>{item.tipo}</span></td>
                              <td className="historial-precio">{Number.isFinite(precioNum) ? clpFmt(precioNum) : item.precio ?? ''}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {datosGrafico.length > 0 && (
                  <div className="detalle-card">
                    <div className="detalle-card-header">Evolución de precio por tipo</div>
                    <div className="detalle-chart-wrapper">
                      <ResponsiveContainer width="100%" height={280}>
                        <LineChart data={datosGrafico} margin={{ top: 8, right: 20, left: 70, bottom: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.12)" />
                          <XAxis dataKey="fecha" tick={{ fontSize: 12 }} />
                          <YAxis domain={['dataMin - 100000', 'dataMax + 100000']} tickFormatter={v => `$${(v / 1_000_000).toFixed(1)}M`} width={70} tick={{ fontSize: 12 }} />
                          <Tooltip /><Legend verticalAlign="top" align="right" wrapperStyle={{ fontSize: 12 }} />
                          {Object.keys(datosGrafico[0] || {}).filter(k => k !== 'fecha').map((tipo, i) => (
                            <Line key={tipo} type="monotone" dataKey={tipo} stroke={CHART_COLORS[i % 4]} strokeWidth={2} dot={{ r: 4 }} />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── TAB: FOB ── */}
            {pestanaActiva === 'fob' && (
              <div className="detalle-tab-content">
                <div className="detalle-state">
                  <Ship size={28} style={{ opacity: 0.3 }} />
                  <p>Precios FOB disponibles próximamente.</p>
                </div>
              </div>
            )}

            {/* ── TAB: Características ── */}
            {pestanaActiva === 'caracteristicas' && (
              <div className="detalle-tab-content">

                {/* Info general: todos los campos del Mantenedor + mi clasificación privada */}
                <div className="detalle-card">
                      <div className="detalle-card-header">
                        Información general
                      </div>
                      <div className="detalle-card-body" style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
                        <div className="spec-kv-card" style={{ flex: '1 1 220px', border: '1px dashed #c4b5fd', background: '#faf5ff' }}>
                          <div className="spec-kv-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Lock size={10} /> Mi clasificación <span style={{ fontWeight: 400, opacity: .7 }}>(solo tú la ves)</span>
                          </div>
                          {editandoClasificacion ? (
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4 }}>
                              <input
                                list="mis-clasificaciones-sugeridas"
                                value={clasificacionInput}
                                onChange={e => setClasificacionInput(e.target.value)}
                                placeholder="Ej: SUV Compacta"
                                autoFocus
                                disabled={guardandoClasificacion}
                                onKeyDown={e => { if (e.key === 'Enter') guardarClasificacion(); if (e.key === 'Escape') setEditandoClasificacion(false); }}
                                style={{ fontSize: 13, padding: '4px 8px', border: '1px solid #c4b5fd', borderRadius: 6, flex: 1, minWidth: 120 }}
                              />
                              <button onClick={guardarClasificacion} disabled={guardandoClasificacion} title="Guardar"
                                style={{ display: 'flex', padding: 5, border: '1px solid #86efac', background: '#f0fdf4', color: '#166534', borderRadius: 6, cursor: 'pointer' }}>
                                <Check size={14} />
                              </button>
                              <button onClick={() => setEditandoClasificacion(false)} disabled={guardandoClasificacion} title="Cancelar"
                                style={{ display: 'flex', padding: 5, border: '1px solid #e5e7eb', background: '#fff', color: '#6b7280', borderRadius: 6, cursor: 'pointer' }}>
                                <X size={14} />
                              </button>
                              <datalist id="mis-clasificaciones-sugeridas">
                                {sugerenciasClasificacion.map(s => <option key={s} value={s} />)}
                              </datalist>
                            </div>
                          ) : (
                            <div className="spec-kv-value" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }} onClick={iniciarEdicionClasificacion}>
                              {miClasificacion || <span style={{ color: '#9ca3af', fontStyle: 'italic', fontWeight: 400 }}>Sin clasificar — click para agregar</span>}
                              <Pencil size={11} style={{ opacity: .5 }} />
                            </div>
                          )}
                        </div>
                        {modeloSeleccionado.categoria && (
                          <div className="spec-kv-card" style={{ flex: '1 1 150px' }}>
                            <div className="spec-kv-label">Categoría</div>
                            <div className="spec-kv-value">{modeloSeleccionado.categoria}</div>
                          </div>
                        )}
                        {modeloSeleccionado.subcategoria && (
                          <div className="spec-kv-card" style={{ flex: '1 1 150px' }}>
                            <div className="spec-kv-label">Subcategoría</div>
                            <div className="spec-kv-value">{modeloSeleccionado.subcategoria}</div>
                          </div>
                        )}
                        {modeloSeleccionado.segmento && (
                          <div className="spec-kv-card" style={{ flex: '1 1 150px' }}>
                            <div className="spec-kv-label">Segmento</div>
                            <div className="spec-kv-value">{modeloSeleccionado.segmento}</div>
                          </div>
                        )}
                        {modeloSeleccionado.origen && (
                          <div className="spec-kv-card" style={{ flex: '1 1 150px' }}>
                            <div className="spec-kv-label">Origen</div>
                            <div className="spec-kv-value">
                              {({
                                'Japonés': '🇯🇵', 'Coreano': '🇰🇷', 'Chino': '🇨🇳', 'Alemán': '🇩🇪',
                                'Americano': '🇺🇸', 'Francés': '🇫🇷', 'Italiano': '🇮🇹', 'Sueco': '🇸🇪',
                                'Checo': '🇨🇿', 'Español': '🇪🇸', 'Inglés': '🇬🇧', 'Indio': '🇮🇳'
                              })[modeloSeleccionado.origen] || ''}{' '}{modeloSeleccionado.origen}
                            </div>
                          </div>
                        )}
                        {modeloSeleccionado.combustible && (
                          <div className="spec-kv-card" style={{ flex: '1 1 150px' }}>
                            <div className="spec-kv-label">Combustible</div>
                            <div className="spec-kv-value">{modeloSeleccionado.combustible}</div>
                          </div>
                        )}
                        {modeloSeleccionado.traccion && (
                          <div className="spec-kv-card" style={{ flex: '1 1 150px' }}>
                            <div className="spec-kv-label">Tracción</div>
                            <div className="spec-kv-value">{modeloSeleccionado.traccion}</div>
                          </div>
                        )}
                      </div>
                </div>

                {loadingSpecs && (
                  <div className="detalle-state"><p>Cargando ficha técnica…</p></div>
                )}
                {!loadingSpecs && !specs && (
                  <div className="detalle-state">
                    <Settings size={28} style={{ opacity: 0.3 }} />
                    <p>No hay ficha técnica disponible para este modelo.</p>
                  </div>
                )}
                {!loadingSpecs && specs && (() => {
                  const s = specs.specs || {};
                  return (
                    <div className="detalle-card">
                      <div className="detalle-card-body">
                        <div className="spec-ficha-header">
                          <div className="spec-ficha-icon"><Settings size={14} /></div>
                          <div>
                            <div className="spec-ficha-version">{specs.vehiculo?.version || specs.version || 'Ficha técnica'}</div>
                            {specs.metadata?.fuente && (
                              <div className="spec-ficha-meta">{specs.metadata.fuente}{specs.metadata?.fecha_extraccion && ` · ${specs.metadata.fecha_extraccion}`}</div>
                            )}
                          </div>
                        </div>

                        {s.motor && (
                          <SpecSection titulo="Motor y transmisión" icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3" /><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83" /></svg>} cols="repeat(auto-fit, minmax(160px, 1fr))">
                            {Object.entries(s.motor).filter(([, v]) => v !== null && v !== undefined && v !== '').map(([k, v]) => <KVCard key={k} label={k} value={v} />)}
                          </SpecSection>
                        )}
                        {s.dimensiones && (
                          <SpecSection titulo="Dimensiones" icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3h18v18H3z" /><path d="M3 9h18M9 3v18" /></svg>} cols="repeat(auto-fit, minmax(160px, 1fr))">
                            {Object.entries(s.dimensiones).filter(([, v]) => v !== null && v !== undefined && v !== '').map(([k, v]) => <KVCard key={k} label={k} value={v} />)}
                          </SpecSection>
                        )}
                        {s.suspension_frenos && (
                          <SpecSection titulo="Suspensión y frenos" icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="3" /></svg>} cols="repeat(auto-fit, minmax(180px, 1fr))">
                            {Object.entries(s.suspension_frenos).filter(([, v]) => v !== null && v !== undefined && v !== '').map(([k, v]) => <KVCard key={k} label={k} value={v} />)}
                          </SpecSection>
                        )}
                        {s.direccion && (
                          <SpecSection titulo="Dirección" icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M12 2v10l4 4" /></svg>} cols="repeat(auto-fit, minmax(200px, 1fr))">
                            {Object.entries(s.direccion).filter(([, v]) => v !== null && v !== undefined && v !== '').map(([k, v]) => <KVCard key={k} label={k} value={v} />)}
                          </SpecSection>
                        )}
                        {s.comparativas?.rendimiento && (() => {
                          const entries = Object.entries(s.comparativas.rendimiento).filter(([, v]) => v !== null && v !== undefined && v !== '');
                          return entries.length ? (
                            <SpecSection titulo="Rendimiento y consumo" icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" /></svg>} cols="repeat(auto-fit, minmax(200px, 1fr))">
                              {entries.map(([k, v]) => <KVCard key={k} label={k} value={v} />)}
                            </SpecSection>
                          ) : null;
                        })()}
                        {s.comparativas?.capacidades && (() => {
                          const entries = Object.entries(s.comparativas.capacidades).filter(([, v]) => v !== null && v !== undefined && v !== '');
                          return entries.length ? (
                            <SpecSection titulo="Capacidades y peso" icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></svg>} cols="repeat(auto-fit, minmax(180px, 1fr))">
                              {entries.map(([k, v]) => typeof v === 'object' && !Array.isArray(v)
                                ? Object.entries(v).filter(([, vv]) => vv !== null && vv !== undefined && vv !== '').map(([k2, v2]) => <KVCard key={`${k}-${k2}`} label={k2} value={v2} />)
                                : <KVCard key={k} label={k} value={v} />)}
                            </SpecSection>
                          ) : null;
                        })()}
                        {s.comparativas?.ruedas && (() => {
                          const entries = Object.entries(s.comparativas.ruedas).filter(([, v]) => v !== null && v !== undefined && v !== '');
                          return entries.length ? (
                            <SpecSection titulo="Ruedas" icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="3" /><line x1="12" y1="2" x2="12" y2="9" /><line x1="12" y1="15" x2="12" y2="22" /><line x1="2" y1="12" x2="9" y2="12" /><line x1="15" y1="12" x2="22" y2="12" /></svg>} cols="repeat(auto-fit, minmax(160px, 1fr))">
                              {entries.map(([k, v]) => <KVCard key={k} label={k} value={v} />)}
                            </SpecSection>
                          ) : null;
                        })()}
                        {s.equipamiento_estandar && (() => {
                          const entries = Object.entries(s.equipamiento_estandar).filter(([, v]) => v && (Array.isArray(v) ? v.length > 0 : true));
                          return entries.length ? (
                            <SpecSection titulo="Equipamiento estándar" icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg>} cols="repeat(auto-fit, minmax(260px, 1fr))">
                              {entries.map(([cat, items]) => <EquipCard key={cat} categoria={cat} items={items} />)}
                            </SpecSection>
                          ) : null;
                        })()}
                        {s.equipamiento_diferencial && (() => {
                          const entries = Object.entries(s.equipamiento_diferencial).filter(([, v]) => v && (Array.isArray(v) ? v.length > 0 : true));
                          return entries.length ? (
                            <SpecSection titulo="Equipamiento diferencial" icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>} cols="repeat(auto-fit, minmax(260px, 1fr))">
                              {entries.map(([cat, items]) => <EquipCard key={cat} categoria={cat} items={items} />)}
                            </SpecSection>
                          ) : null;
                        })()}
                        {s.colores && Array.isArray(s.colores) && s.colores.length > 0 && (
                          <SpecSection titulo="Colores disponibles" icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /></svg>} cols="1fr">
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                              {s.colores.map((color, i) => <span key={i} className="spec-color-pill">{color}</span>)}
                            </div>
                          </SpecSection>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* ── TAB: Usados ── */}
            {pestanaActiva === 'usados' && (
              <div className="detalle-tab-content">
                {loadingUsados && <div className="detalle-state"><p>Cargando usados…</p></div>}

                {!loadingUsados && resumenUsados && (
                  <div className="detalle-stats-grid">
                    <div className="detalle-stat-card detalle-stat-card--blue">
                      <div className="detalle-stat-label">Total publicados</div>
                      <div className="detalle-stat-value">{resumenUsados.total}</div>
                    </div>
                    <div className="detalle-stat-card detalle-stat-card--green">
                      <div className="detalle-stat-label">Precio mediana</div>
                      <div className="detalle-stat-value--md detalle-stat-value">{clpFmt(resumenUsados.precioMediana)}</div>
                      <div className="detalle-stat-sub">{clpFmt(resumenUsados.precioMin)} – {clpFmt(resumenUsados.precioMax)}</div>
                      <div className="detalle-stat-sub">Prom: {clpFmt(resumenUsados.precioAvg)}</div>
                    </div>
                    <div className="detalle-stat-card detalle-stat-card--amber">
                      <div className="detalle-stat-label">Kilometraje prom.</div>
                      <div className="detalle-stat-value--md detalle-stat-value">{new Intl.NumberFormat('es-CL').format(resumenUsados.kmAvg)} km</div>
                      <div className="detalle-stat-sub">{new Intl.NumberFormat('es-CL').format(resumenUsados.kmMin)} – {new Intl.NumberFormat('es-CL').format(resumenUsados.kmMax)} km</div>
                      <div className="detalle-stat-sub">Años: {resumenUsados.anioMin ?? '—'} – {resumenUsados.anioMax ?? '—'}</div>
                    </div>
                  </div>
                )}

                {!loadingUsados && ratioDepreciacion && (
                  <div className="detalle-depreciation">
                    <div>
                      <div className="detalle-stat-label">Depreciación anual</div>
                      <div className="detalle-stat-value--md detalle-stat-value" style={{ color: '#ef4444' }}>{clpFmt(ratioDepreciacion.depAnualCLP)}</div>
                      <div className="detalle-stat-sub">{ratioDepreciacion.depAnualPct}% anual</div>
                    </div>
                    <div>
                      <div className="detalle-stat-label">Por kilómetro</div>
                      <div className="detalle-stat-value--md detalle-stat-value">{ratioDepreciacion.depPorKmCLP.toFixed(0)} CLP/km</div>
                      <div className="detalle-stat-sub">n={ratioDepreciacion.n} · ref {ratioDepreciacion.ageRef} años, {new Intl.NumberFormat('es-CL').format(ratioDepreciacion.kmRef)} km</div>
                    </div>
                  </div>
                )}

                {!loadingUsados && !ratioDepreciacion && usados?.length > 0 && (
                  <p style={{ fontSize: 13, color: '#9ca3af' }}>Sin suficiente data para depreciación (mínimo 5 autos con precio, km y año).</p>
                )}

                {!loadingUsados && !resumenUsados && (
                  <div className="detalle-state">
                    <DollarSign size={28} style={{ opacity: 0.3 }} />
                    <p>No hay datos de usados para este modelo.</p>
                  </div>
                )}

                {datosGraficoUsadosPrecio.length > 0 && (
                  <div className="detalle-card">
                    <div className="detalle-card-header">Precio promedio por año</div>
                    <div className="detalle-chart-wrapper">
                      <ResponsiveContainer width="100%" height={240}>
                        <LineChart data={datosGraficoUsadosPrecio} margin={{ top: 8, right: 20, left: 70, bottom: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.12)" />
                          <XAxis dataKey="anio" tick={{ fontSize: 12 }} />
                          <YAxis tickFormatter={v => `$${(v / 1_000_000).toFixed(1)}M`} width={70} tick={{ fontSize: 12 }} />
                          <Tooltip formatter={v => clpFmt(v)} />
                          <Legend verticalAlign="top" align="right" wrapperStyle={{ fontSize: 12 }} />
                          <Line type="monotone" dataKey="precioProm" name="Precio promedio" stroke="#378ADD" strokeWidth={2} dot={{ r: 4 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {datosGraficoUsadosKm.length > 0 && (
                  <div className="detalle-card">
                    <div className="detalle-card-header">Kilometraje y cantidad por año</div>
                    <div className="detalle-chart-wrapper">
                      <ResponsiveContainer width="100%" height={240}>
                        <LineChart data={datosGraficoUsadosKm} margin={{ top: 8, right: 20, left: 70, bottom: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.12)" />
                          <XAxis dataKey="anio" tick={{ fontSize: 12 }} />
                          <YAxis width={70} tick={{ fontSize: 12 }} />
                          <Tooltip />
                          <Legend verticalAlign="top" align="right" wrapperStyle={{ fontSize: 12 }} />
                          <Line type="monotone" dataKey="kmProm" name="KM promedio" stroke="#1D9E75" strokeWidth={2} dot={{ r: 4 }} />
                          <Line type="monotone" dataKey="cantidad" name="Cantidad" stroke="#EF9F27" strokeWidth={2} dot={{ r: 4 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {usados.length > 0 && (
                  <div className="detalle-card">
                    <div className="detalle-card-header">
                      Publicaciones
                      {usados.length > 50 && <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: 8 }}>— mostrando 50 de {usados.length}</span>}
                    </div>
                    <div className="usados-tabla-wrap">
                      <table className="usados-tabla">
                        <thead>
                          <tr>
                            {['Marca', 'Modelo', 'Versión', 'Año', 'Km', 'Combustible', 'Transmisión', 'Precio', 'Link'].map(h => (
                              <th key={h} style={{ textAlign: h === 'Precio' ? 'right' : 'left' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {usados.slice(0, 50).map((u, idx) => {
                            const precioNum = typeof u.precio === 'string' ? Number(u.precio.replace(/\D+/g, '')) || 0 : Number(u.precio || 0) || 0;
                            return (
                              <tr key={u.id}>
                                <td>{u.marca ?? u.brand ?? '—'}</td>
                                <td>{u.model ?? u.modelo ?? '—'}</td>
                                <td style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={String(u.version ?? u.modelDetail ?? '—')}>{u.version ?? u.versionDetail ?? u.modelDetail ?? '—'}</td>
                                <td>{u.anio ?? u.year ?? '—'}</td>
                                <td>{u.kilometraje ? new Intl.NumberFormat('es-CL').format(u.kilometraje) : '—'}</td>
                                <td>{u.combustible ?? u.fuel ?? '—'}</td>
                                <td>{u.transmision ?? u.transmission ?? '—'}</td>
                                <td style={{ textAlign: 'right', fontWeight: 600 }}>{clpFmt(precioNum)}</td>
                                <td style={{ textAlign: 'center' }}>
                                  {(u.fuente || u.url || u.link)
                                    ? <a href={u.fuente || u.url || u.link} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', fontWeight: 500, fontSize: 12 }}>Ver →</a>
                                    : <span style={{ color: '#d1d5db' }}>—</span>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
}

export default DetalleModelo;